/**
 * Visual settings persisted to localStorage and reflected onto the
 * <html> element via data-theme / data-fontset / data-density /
 * data-motion attributes.
 *
 * Defaults: cobalt theme (Cobalt Tape), SF Mono + Space Grotesk
 * fontset, comfortable density, auto motion.
 *
 * `theme` is the single color axis: `cobalt` (default) or `plasma`.
 * The pre-mount bootstrap in index.html applies it before first paint
 * so there's no flash; this hook owns runtime changes + cross-tab sync.
 */

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../lib/constants';
import {
  Theme, Fontset, Density, Motion, VisualSettings,
} from '../lib/types';

const DEFAULTS: VisualSettings = {
  theme: 'cobalt',
  fontset: 'sf',
  density: 'comfortable',
  motion: 'auto',
};

// theme-color matches each theme's page ground (keeps the mobile browser
// chrome in sync with the app background).
const THEME_COLORS: Record<Theme, string> = {
  cobalt: '#0a1220',
  plasma: '#08070d',
};

// Coerce any stored value (including stale legacy/v2/cyan/bloomberg keys
// from the previous dual-axis system) to a valid theme. Anything we don't
// recognise falls back to the default so nothing renders unstyled.
function coerceTheme(v: string | null): Theme {
  return v === 'plasma' ? 'plasma' : 'cobalt';
}

function readStored(): VisualSettings {
  try {
    return {
      theme:   coerceTheme(localStorage.getItem(STORAGE_KEYS.theme)),
      fontset: (localStorage.getItem(STORAGE_KEYS.fontset) as Fontset) || DEFAULTS.fontset,
      density: (localStorage.getItem(STORAGE_KEYS.density) as Density) || DEFAULTS.density,
      motion:  (localStorage.getItem(STORAGE_KEYS.motion)  as Motion)  || DEFAULTS.motion,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function applyToHtml(s: VisualSettings) {
  const html = document.documentElement;
  // Always set theme so CSS can target [data-theme=...] unconditionally.
  html.dataset.theme = s.theme;

  if (s.fontset === 'sf') delete html.dataset.fontset;
  else html.dataset.fontset = s.fontset;

  html.dataset.density = s.density;
  html.dataset.motion  = s.motion;

  // Keep the browser theme-color in sync when the theme flips at runtime.
  // (Initial load is handled by the inline bootstrap in index.html so
  // there's no first-paint flash.)
  if (typeof document !== 'undefined') {
    const theme = document.querySelector('meta[name="theme-color"]');
    if (theme) theme.setAttribute('content', THEME_COLORS[s.theme]);
  }
}

function persist<K extends keyof VisualSettings>(key: K, value: VisualSettings[K]) {
  const storageKey = (
    key === 'theme'   ? STORAGE_KEYS.theme :
    key === 'fontset' ? STORAGE_KEYS.fontset :
    key === 'density' ? STORAGE_KEYS.density :
    STORAGE_KEYS.motion
  );
  try {
    // We always persist — even if it equals the default — so flipping
    // back to the default value is a deliberate choice rather than a
    // "removed key" silently inheriting a future default change.
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* localStorage unavailable */
  }
}

// The set of localStorage keys that belong to this hook — used by the
// cross-tab listener to decide whether an incoming storage event is ours.
// Typed as Set<string> so Set.has() accepts the runtime `e.key: string`.
const VISUAL_STORAGE_KEYS: Set<string> = new Set([
  STORAGE_KEYS.theme,
  STORAGE_KEYS.fontset,
  STORAGE_KEYS.density,
  STORAGE_KEYS.motion,
]);

export function useVisualSettings() {
  const [settings, setSettings] = useState<VisualSettings>(() => readStored());

  useEffect(() => { applyToHtml(settings); }, [settings]);

  // Cross-tab sync: when another tab writes a visual-settings key, adopt
  // the change here without writing back to localStorage (that would cause
  // a feedback loop between tabs).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key || !VISUAL_STORAGE_KEYS.has(e.key)) return;
      const next = readStored();
      setSettings(prev => {
        // Skip unnecessary re-renders when nothing actually changed.
        if (
          prev.theme   === next.theme &&
          prev.fontset === next.fontset &&
          prev.density === next.density &&
          prev.motion  === next.motion
        ) return prev;
        return next;
      });
      // Also push the updated attributes onto <html> immediately so CSS
      // repaints without waiting for the next render cycle.
      applyToHtml(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    setSettings(prev => { persist('theme', theme); return { ...prev, theme }; });
  }, []);
  const setFontset = useCallback((fontset: Fontset) => {
    setSettings(prev => { persist('fontset', fontset); return { ...prev, fontset }; });
  }, []);
  const setDensity = useCallback((density: Density) => {
    setSettings(prev => { persist('density', density); return { ...prev, density }; });
  }, []);
  const setMotion = useCallback((motion: Motion) => {
    setSettings(prev => { persist('motion', motion); return { ...prev, motion }; });
  }, []);
  const reset = useCallback(() => {
    // Reset fontset/density/motion to defaults but preserve the user's
    // chosen theme — a deliberate theme pick shouldn't be lost on a
    // font/density/motion reset.
    setSettings(prev => {
      persist('fontset', DEFAULTS.fontset);
      persist('density', DEFAULTS.density);
      persist('motion',  DEFAULTS.motion);
      return { ...DEFAULTS, theme: prev.theme };
    });
  }, []);

  return {
    ...settings,
    setTheme, setFontset, setDensity, setMotion, reset,
  };
}
